import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              <Shield className="w-3 h-3 mr-1" />
              Privacy Policy
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Privacy <span className="text-accent">Policy</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Last Updated: December 25, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-slate">
            <div className="space-y-8 text-muted-foreground">
              
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="leading-relaxed">
                  Shoot Base ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform to connect film professionals with production houses across India.
                </p>
                <p className="leading-relaxed mt-3">
                  By using Shoot Base, you agree to the collection and use of information in accordance with this policy. This policy is designed to comply with the Digital Personal Data Protection Act, 2023 (DPDP Act) and other applicable Indian data protection laws.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">2.1 Personal Information</h3>
                <p className="leading-relaxed">When you create an account, we collect:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Name, email address, and phone number</li>
                  <li>Professional details (experience, specialization, department)</li>
                  <li>Location information (city, state)</li>
                  <li>Portfolio images and work samples</li>
                  <li>Profile photo and bio</li>
                  <li>Payment information (if applicable)</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, time spent, clicks)</li>
                  <li>Cookies and similar tracking technologies</li>
                  <li>Log data (timestamps, errors, performance metrics)</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">2.3 Information from Third Parties</h3>
                <p className="leading-relaxed">
                  We may receive information about you from third-party services such as social media platforms if you choose to connect your accounts.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">3. How We Use Your Information</h2>
                <p className="leading-relaxed">We use your information to:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Provide, operate, and maintain our platform</li>
                  <li>Create and manage your account</li>
                  <li>Connect professionals with production houses</li>
                  <li>Process transactions and send notifications</li>
                  <li>Verify credentials and maintain platform integrity</li>
                  <li>Improve our services and develop new features</li>
                  <li>Send administrative information and updates</li>
                  <li>Respond to inquiries and provide customer support</li>
                  <li>Detect, prevent, and address technical issues or fraud</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">4. Information Sharing and Disclosure</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.1 Public Profile Information</h3>
                <p className="leading-relaxed">
                  Your professional profile, including name, bio, portfolio, location, and specialization, is visible to other users (clients and professionals) on the platform. You control what information is publicly displayed.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.2 Service Providers</h3>
                <p className="leading-relaxed">
                  We may share your information with third-party service providers who perform services on our behalf, including:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Cloud hosting providers (data storage)</li>
                  <li>Email service providers (communications)</li>
                  <li>Analytics providers (usage insights)</li>
                  <li>Payment processors (transaction handling)</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.3 Legal Requirements</h3>
                <p className="leading-relaxed">
                  We may disclose your information if required by law or in response to valid legal requests from public authorities.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.4 Business Transfers</h3>
                <p className="leading-relaxed">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">5. Cookies and Tracking Technologies</h2>
                <p className="leading-relaxed">
                  We use cookies and similar tracking technologies to track activity on our platform and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of our platform may not function properly.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">6. Data Security</h2>
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal data, we cannot guarantee absolute security.
                </p>
                <p className="leading-relaxed mt-3">
                  Security measures include:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security audits and updates</li>
                  <li>Access controls and authentication</li>
                  <li>Secure backup procedures</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">7. Your Rights Under DPDP Act</h2>
                <p className="leading-relaxed">Under the Digital Personal Data Protection Act, 2023, you have the following rights:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>Right to Access:</strong> Request access to your personal data we hold</li>
                  <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete data</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                  <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
                  <li><strong>Right to Grievance Redressal:</strong> Lodge complaints regarding data processing</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  To exercise these rights, contact us at <a href="mailto:privacy@shootbase.in" className="text-accent hover:underline">privacy@shootbase.in</a>
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">8. Data Retention</h2>
                <p className="leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. When you delete your account, we will delete or anonymize your personal data, except for data we are required to retain for legal, regulatory, or security purposes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">9. Children's Privacy</h2>
                <p className="leading-relaxed">
                  Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">10. International Data Transfers</h2>
                <p className="leading-relaxed">
                  Your information may be transferred to and maintained on servers located outside of India. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">11. Changes to This Privacy Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">12. Contact Us</h2>
                <p className="leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="mt-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
                  <p className="font-medium text-foreground">Shoot Base</p>
                  <p>Email: <a href="mailto:privacy@shootbase.in" className="text-accent hover:underline">privacy@shootbase.in</a></p>
                  <p>Address: Film City, Mumbai, Maharashtra, India</p>
                  <p>Phone: +91 12345 67890</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
